import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import filmMp4 from "../../media/story/founder-film.mp4";
import filmPoster from "../../media/story/founder-film-poster.webp";

// The founder film player, on its own so the home page and /about share ONE
// implementation of the autoplay, sound and reduced-motion rules below rather
// than two copies that drift. Extracted from FounderFilm.tsx unchanged in
// behaviour; that component still owns the surrounding section, copy and marks.
//
// HOSTING: a plain static MP4 on our OWN origin, imported so Vite fingerprints
// it and serves it from the same CDN as the rest of the build. No YouTube, no
// Vimeo, no iframe, no third-party player script. A video file is inert bytes
// with no execution path through it and no external origin in the page.
//
// PLAYBACK: nothing plays until the player is actually on screen. It is muted,
// inline, and loops, so arriving on a page is silent. Browsers refuse unmuted
// autoplay anyway, and a video that starts on load burns data on something the
// visitor may never scroll to.
//
// SOUND: the captions are burned into the picture, so muted playback carries
// the whole message and the unmute button is a genuine extra.
//
// preload="metadata" fetches a few KB (dimensions and duration) rather than the
// file. The bytes are only pulled when the player is approached.

type Props = {
  /** Width classes for the card. Home uses the compact default; /about, where
   *  the film IS the section, passes something larger. */
  className?: string;
};

const FounderFilmPlayer: React.FC<Props> = ({
  className = "max-w-[300px] md:max-w-[340px]",
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Honour Reduce Motion: leave it paused with controls, never auto-start.
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      setReduced(true);
      return;
    }

    // IntersectionObserver is right here, unlike in Reveal.tsx, which had to
    // abandon it. An element already scrolled PAST at hydration never
    // intersects again, and for playback that is correct: a section the reader
    // has gone by should not start playing. We also need the exit event, which
    // is exactly what an observer gives us.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // play() rejects if the browser declines. Swallow it: a still frame
          // on the poster is a fine outcome, an unhandled rejection is not.
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      // Half the video has to be on screen, so it does not start as a sliver.
      { threshold: 0.5 },
    );
    io.observe(el);

    const onVisibility = () => {
      if (document.hidden) el.pause();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
    if (!el.muted) el.play().catch(() => {});
  };

  return (
    <div className={`relative mx-auto w-full ${className}`}>
      <div className="rotate-1 rounded-[28px] overflow-hidden shadow-[0_30px_60px_-24px_rgba(45,56,73,0.5)] bg-ink/5">
        <video
          ref={videoRef}
          poster={filmPoster}
          muted
          loop
          playsInline
          preload="metadata"
          controls={reduced}
          aria-label="Josh and Adrien on why they made LetsLoop, filmed around London. Captions are part of the picture."
          className="block w-full h-auto"
        >
          <source src={filmMp4} type="video/mp4" />
        </video>
      </div>

      {!reduced && (
        <button
          type="button"
          onClick={toggleSound}
          aria-pressed={!muted}
          className="absolute bottom-4 right-4 z-10 inline-flex items-center gap-2 rounded-full bg-ink/70 text-cream backdrop-blur px-4 py-2.5 text-sm font-semibold hover:bg-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          {muted ? "Sound on" : "Sound off"}
        </button>
      )}
    </div>
  );
};

export default FounderFilmPlayer;
